<script lang="ts">
	import Header from '$lib/Header.svelte';
	import SignInForm from '$lib/SignInForm.svelte';
	import { serviceContext } from '$lib/ServiceContext';
	import { Button } from '$lib/shadcn/components/ui/button';
	import * as Accordion from '$lib/shadcn/components/ui/accordion';
	import HydrationFallback from '$lib/components/HydrationFallback.svelte';
	import Skeleton from '$lib/shadcn/components/ui/skeleton/skeleton.svelte';
	import { ShieldCheck } from '@lucide/svelte';
	import PricingCard from '../PricingCard.svelte';
	import { pricing } from '$lib/pricing';
	import { onMount } from 'svelte';
	import { SignInState } from '$lib/AuthService.svelte';
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/state';
	import { resolveLocale } from '$lib/utils.svelte';

	let signInInProgress = $state(false);

	let isPluginLicensed = $state<Promise<boolean>>();

	onMount(() => {
		return serviceContext.authService.onAuthStateChanged(() => {
			isPluginLicensed = serviceContext.userService.isPluginLicensed().catch(() => false);
		});
	});
</script>

<SvelteSeo
	title="Download TightStacks | Vocal Alignment Plugin"
	description="Download TightStacks for macOS and Windows. Compatible with Ableton Live, Logic Pro, Pro Tools, FL Studio, and more. VST3 & AU."
/>

<Header />
<main class="flex items-center justify-center p-4 lg:p-8">
	<div class="flex w-full flex-col gap-4 md:px-28 lg:gap-6 lg:px-36 xl:px-40 2xl:max-w-7xl">
		<div class="flex flex-col">
			<p class="preset-typo-title">Installation Instructions</p>
			<ol class="ml-4 list-decimal">
				<li>Download plugin</li>
				<li>
					Windows: Copy VST to your plugins folder <span class="opacity-75 hover:opacity-100"
						>(e.g. C:\Program Files\Common Files\VST3)</span
					>
				</li>
				<li>MacOS: Run installer</li>
				<li>Sign into your account</li>
			</ol>
		</div>
		<div class="flex flex-col">
			<h3 class="preset-typo-title">System Requirements</h3>
			<ul class="ml-4 list-disc">
				<li>Windows 10 or 11 (64bit) / macOS 11 or later (Intel/ARM64) (64bit)</li>
				<li>DAW with VST3/AU support</li>
			</ul>
		</div>
		{#snippet skeleton()}
			<div class="flex flex-col gap-2">
				<Skeleton class="h-16 w-full" />
				<Skeleton class="h-12 w-full" />
				<Skeleton class="h-12 w-full" />
				<Skeleton class="h-10 w-full" />
			</div>
		{/snippet}
		<HydrationFallback {skeleton}>
			{#await serviceContext.authService.authReady()}
				{@render skeleton()}
			{:then}
				{#if serviceContext.authService.getSignInState() == SignInState.SignedOut || signInInProgress}
					<div class="flex flex-col gap-2">
						<p class="preset-typo-body-1 text-center">
							Sign in to download or purchase TightStacks
						</p>
						<SignInForm
							onBegin={async () => {
								signInInProgress = true;
							}}
							afterSuccess={async () => {
								signInInProgress = false;
							}}
							onFailure={async () => {
								signInInProgress = false;
							}}
						/>
					</div>
				{:else}
					{#await isPluginLicensed || new Promise(() => {})}
						{@render skeleton()}
					{:then isPluginLicensed}
						{#if !isPluginLicensed}
							<div class="flex flex-col gap-4">
								<div>
									<h3 class="preset-typo-title">Purchase</h3>
								</div>
								<div class="flex flex-col items-center justify-center gap-4">
									<div class="flex flex-col items-center justify-center gap-4 lg:flex-row">
										<PricingCard
											highlighted
											title="Lifetime"
											description="Provides lifetime access to the TightStacks plugin with a single purchase and no recurring costs."
											price={pricing.lifetime.price}
											priceCents={pricing.lifetime.priceCents}
											fullPrice={pricing.lifetime.fullPrice}
											fullPriceCents={pricing.lifetime.fullPriceCents}
											salePercentage={pricing.lifetime.salePercentage}
											duration="/ once"
											purchaseId={pricing.lifetime.purchaseId}
											purchaseMode="payment"
											buttonText="Purchase"
											checkItems={['Full access', 'All updates', 'No subscription']}
											returnUrl={page.url.pathname}
											successUrl={resolveLocale('/(webapp)/[[locale=locale]]/checkout/success')}
											onclick={() =>
												serviceContext.analyticsService.initiatePurchase('Lifetime', 20)}
										/>
										<PricingCard
											title="Subscription"
											description="Provides flexible access to the TightStacks plugin based on a monthly recurring subscription."
											price={pricing.monthly.price}
											priceCents={pricing.monthly.priceCents}
											fullPrice={pricing.monthly.fullPrice}
											fullPriceCents={pricing.monthly.fullPriceCents}
											salePercentage={pricing.monthly.salePercentage}
											duration="/ month"
											purchaseId={pricing.monthly.purchaseId}
											purchaseMode="subscription"
											buttonText="Subscribe"
											checkItems={['Full access', 'All updates', 'Cancel anytime']}
											returnUrl={page.url.pathname}
											successUrl={resolveLocale('/(webapp)/[[locale=locale]]/checkout/success')}
											onclick={() =>
												serviceContext.analyticsService.initiatePurchase('Subscription', 1)}
										/>
									</div>
									<p class="text-muted-foreground-2 lg:text-md flex items-center gap-2 text-xs">
										<ShieldCheck class="size-3 lg:size-4" /> All Licenses come with a 30 day money back
										guarantee.
									</p>
								</div>
							</div>
						{:else}
							<div class="flex flex-col gap-2">
								<h3 class="preset-typo-title">Downloads</h3>
								<div class="flex flex-col gap-4">
									<Button
										href="https://storage.googleapis.com/voicealign-binaries/TightStacks.vst3"
										download
										variant="filled"
										size="lg"
										onclick={() => serviceContext.analyticsService.downloadPlugin()}
										><span class="hidden sm:inline">Download </span> Plugin Windows (VST3)</Button
									>
									<Button
										href="https://storage.googleapis.com/voicealign-binaries/TightStacks-1.2.2.pkg"
										download
										variant="filled"
										size="lg"
										onclick={() => serviceContext.analyticsService.downloadPlugin()}
										><span class="hidden sm:inline">Download </span> Plugin macOS (VST3/AU)</Button
									>
									<Accordion.Root type="single" class="preset-tonal rounded">
										<!-- @wc-ignore -->
										<Accordion.Item>
											<Accordion.Trigger class="hover:preset-tonal p-2">Changelog</Accordion.Trigger
											>
											<Accordion.Content class="p-2">
												<div class="flex flex-col gap-2">
													<h4>Version 1.2.2</h4>
													<ul class="list-inside list-disc">
														<li>ARA Mode: Fix incompatibility with Logic Pro</li>
													</ul>
													<h4>Version 1.2.1</h4>
													<ul class="list-inside list-disc">
														<li>ARA Mode: Fix crash affecting Cubase 15 users</li>
													</ul>
													<h4>Version 1.2.0</h4>
													<ul class="list-inside list-disc">
														<li>Add auto generated spanish translations</li>
														<li>Improve slow login process</li>
														<li>
															UI now shows clipped waveforms instead of normalizing to the peak
															value
														</li>
														<li>Fix log in issues affecting some users</li>
													</ul>
													<h4>Version 1.1.1</h4>
													<ul class="list-inside list-disc">
														<li>Fix crash affecting some users using AutoTune</li>
													</ul>
													<h4>Version 1.1.0</h4>
													<ul class="list-inside list-disc">
														<li>Add Signalsmith time stretching algorithm</li>
														<li>ARA Mode: Fix UI flickering</li>
														<li>ARA Mode: Fix audio being reduced by 3dB</li>
													</ul>
													<h4>Version 1.0.0</h4>
													<ul class="list-inside list-disc">
														<li>Initial release version</li>
														<li>Fix crash affecting some users</li>
													</ul>
													<h4>Version 0.3.1</h4>
													<ul class="list-inside list-disc">
														<li>Renamed to TightStacks</li>
													</ul>
													<h4>Version 0.3.0</h4>
													<ul class="list-inside list-disc">
														<li>Renamed to TrackSnap</li>
														<li>Speed up plugin sign in</li>
														<li>FL Studio: Fix issue with UI going blank</li>
													</ul>
													<h4>Version 0.2.12</h4>
													<ul class="list-inside list-disc">
														<li>ARA Mode: Fix updates to audio files not reflecting in plugin</li>
													</ul>
													<h4>Version 0.2.11</h4>
													<ul class="list-inside list-disc">
														<li>More bug fixes</li>
													</ul>
													<h4>Version 0.2.10</h4>
													<ul class="list-inside list-disc">
														<li>Extend early access until 06/30/26</li>
													</ul>
													<h4>Version 0.2.9</h4>
													<ul class="list-inside list-disc">
														<li>More bug fixes</li>
													</ul>
													<h4>Version 0.2.8</h4>
													<ul class="list-inside list-disc">
														<li>More bug fixes</li>
													</ul>
													<h4>Version 0.2.7</h4>
													<ul class="list-inside list-disc">
														<li>Clarify upcoming end of early access</li>
														<li>Extend early access until 03/31/26</li>
													</ul>
													<h4>Version 0.2.6</h4>
													<ul class="list-inside list-disc">
														<li>Capture Mode: Allow importing tracks</li>
														<li>Capture Mode: More bug fixes</li>
													</ul>
													<h4>Version 0.2.5</h4>
													<ul class="list-inside list-disc">
														<li>Capture Mode: Allow exporting tracks to .wav</li>
													</ul>
													<h4>Version 0.2.4</h4>
													<ul class="list-inside list-disc">
														<li>Capture Mode: Fix drop out issues during capturing and playback</li>
													</ul>
													<h4>Version 0.2.3</h4>
													<ul class="list-inside list-disc">
														<li>Capture Mode: Fix bypass button not working correctly</li>
													</ul>
													<h4>Version 0.2.2</h4>
													<ul class="list-inside list-disc">
														<li>Introduce VST3/AU plugins for macOS</li>
														<li>Improve UI responsiveness</li>
														<li>Capture Mode: Respect sample rate changes</li>
													</ul>
													<h4>Version 0.2.1</h4>
													<ul class="list-inside list-disc">
														<li>Classify as effect plugin</li>
														<li>Fix visual issues</li>
													</ul>
													<h4>Version 0.2.0</h4>
													<ul class="list-inside list-disc">
														<li>Introduce Capture Mode for DAWs without ARA support</li>
													</ul>
												</div>
											</Accordion.Content>
										</Accordion.Item>
									</Accordion.Root>
								</div>
							</div>
						{/if}
					{/await}
				{/if}
			{/await}
		</HydrationFallback>
	</div>
</main>
